import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import PieChart from './PieChart';
import './Fire.scss';
import calendar from '../../../assets/icons/calendar-icon.png';
import alertsIcon from '../../../assets/icons/bell-icon.png';
import locationIcon from '../../../assets/icons/location-icon.png';
import moment from 'moment-timezone';
import LiveTime from './LiveTime';
import { useAlertsContext } from '../../../contexts/AlertsContext';
import optionsIcon from '../../../assets/icons/options-icon.png';
import { WarningCircle, MapPin, BellSimpleRingingIcon } from '@phosphor-icons/react';
import fireContainedUrl from '../../../assets/icons/fire-gray.png';
import fireHalfContainedUrl from '../../../assets/icons/fire-half-gray.png';
import fireCircleUrl from '../../../assets/icons/fire-red-orange.png';
import fireAlmostGray from '../../../assets/icons/fire-almost-gray.png';
import axios from 'axios';
import Spinner from '../../Spinner';
// Dynamic flag icon loading - no need to manually import each flag

export default function Fire(props) {
    const {
        alerts
    } = useAlertsContext();

    const { 
        setPanel,
        fire,
        user,
        setModal
    } = props;

    const userSub = user?.accessToken.payload.sub;
    const [location, setLocation] = useState(null);
    const [country, setCountry] = useState(null);
    const [flagIcon, setFlagIcon] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const fireRef = useRef(null);

    const fetchLocation = async () => {
        console.log(`🌍 Fetching location for fire: ${fire.name} (${fire.longitude}, ${fire.latitude})`);
        try {
          const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${fire.longitude},${fire.latitude}.json`;
  
          const { data } = await axios.get(url, {
            params: {
              access_token:
                'pk.eyJ1IjoiY2VzYXJtbG5keiIsImEiOiJjbHJ0b2JqZjEwOHpiMmtxa25ubzZ0ZXE3In0.18DfsEFZiecc6H8jHdMOIQ'
            }
          });
  
          const context = data.features?.[0]?.context || [];
          let city = '';
          let state = '';
          let countryCode = '';
  
          context.forEach((c) => {
            if (c.id.includes('place')) {
              city = c.text;
            } else if (c.id.includes('region')) {
              state = c.text;
            } else if (c.id.includes('country')) {
              countryCode = c.short_code?.toLowerCase();
            }
          });
  
          setLocation(city && state ? `${city}, ${state}` : '');
          setCountry(countryCode);
          
          // Load flag icon dynamically if country code is available
          if (countryCode) {
            loadFlagIcon(countryCode);
          }
        } catch (err) {
          console.error('Mapbox geocoding failed:', err);
          setLocation('');
          setCountry(null);
          setFlagIcon(null);
        }
    };

    // Intersection Observer to detect when Fire component is visible
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isVisible) {
                    console.log(`🔥 Fire "${fire.name}" became visible - starting location fetch`);
                    setIsVisible(true);
                }
            },
            {
                threshold: 0.1, // Trigger when 10% of the component is visible
                rootMargin: '50px' // Start loading 50px before it becomes visible
            }
        );

        if (fireRef.current) {
            observer.observe(fireRef.current);
        }

        return () => {
            if (fireRef.current) {
                observer.unobserve(fireRef.current);
            }
        };
    }, [isVisible]);

    // Only fetch location when component becomes visible
    useEffect(() => {
        if (isVisible) {
            // Reset all location-related state when fire changes
            setLocation(null);
            setCountry(null);
            setFlagIcon(null);
            fetchLocation();
        }
    }, [fire.fire_id, isVisible]); // Reset when fire_id changes or becomes visible

    const returnFireIcon = useCallback((perContained) => {
        const pc = perContained ?? 0;
      
        if (pc < 41)          return fireCircleUrl;            // 0–40
        if (pc < 91)          return fireHalfContainedUrl;     // 41–90
        if (pc < 100)         return fireAlmostGray;           // 91–99
        /* pc === 100 */      return fireContainedUrl;         // 100
    }, []);

    const timeDifference = (startTimestamp, endTimestamp) => {
        const start = new Date(startTimestamp);
        const end = new Date(endTimestamp);

        const diffMs = Math.abs(end - start); // Difference in milliseconds

        const diffSeconds = Math.floor(diffMs / 1000) % 60;
        const diffMinutes = Math.floor(diffMs / (1000 * 60)) % 60;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60)) % 24;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        const parts = [];
        if (diffDays > 0) parts.push(`${diffDays} day${diffDays > 1 ? 's' : ''}`);
        if (diffHours > 0) parts.push(`${diffHours} hour${diffHours > 1 ? 's' : ''}`);
        if (diffMinutes > 0) parts.push(`${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`);
        if (diffSeconds > 0) parts.push(`${diffSeconds} second${diffSeconds > 1 ? 's' : ''}`);

        return parts.length > 0 ? parts.join(', ') : '0 seconds';
    }

    const loadFlagIcon = async (countryCode) => {
        if (!countryCode) return;
        
        try {
            // Dynamically import the flag icon based on country code
            const flagModule = await import(`../../../assets/icons/flag-icons-main/flags/4x3/${countryCode}.svg`);
            setFlagIcon(flagModule.default);
        } catch (error) {
            console.log(`Flag icon for ${countryCode} not found, using fallback`);
            // If the specific flag doesn't exist, we could set a default flag or null
            setFlagIcon(null);
        }
    }

    const fireContainmentChange = (oldPercentage, newPercentage) => {
        const difference = newPercentage - oldPercentage;
      
        const changeType = difference > 0 ? "increased" : "decreased";
        const absoluteDifference = Math.abs(difference);
      
        return `${changeType} by ${absoluteDifference}%`;
    }

    const formatDate = useCallback((timestamp) => {
        const time = moment.utc(timestamp).local();
        return time.format('YYYY-MM-DD hh:mm:ss A');
    }, []);

    const getUploaderText = useMemo(() => {
        const { fire_id, cognito_user_id } = fire;
        const userSub = user?.accessToken.payload.sub;
    
        if (!fire_id.toUpperCase().includes('USER-')) {
            return `Source: ${fire.data_source ? fire.data_source.replaceAll('(x-weather)', '') : 'Not Available'}`;
        }
    
        if (cognito_user_id === userSub) {
            return 'Uploaded By You';
        }
    
        return 'Uploaded By Community User';
    }, [fire.fire_id, fire.cognito_user_id, fire.data_source, user?.accessToken.payload.sub]);

    const goToFire = useCallback(() => {
        setPanel({
            name: 'Fire PopUp',
            content: fire
        });
    }, [setPanel, fire]);

    const fireIcon = useMemo(() => returnFireIcon(fire.perContained), [returnFireIcon, fire.perContained]);
    
    const hasAlerts = useMemo(() => 
        alerts.filter((a) => a.incident_id === fire.fire_id).length > 0,
        [alerts, fire.fire_id]
    );

    return (
        <div className='fire-card' ref={fireRef}>
            {/* <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: 4, background: fire.perContained === 0 ? 'red' : !fire.perContained ? 'gray' : fire.perContained <= 40 ? 'red' : (fire.perContained >= 41 && fire.perContained <= 80) ? 'rgb(232, 221, 2)' : 'rgb(66, 183, 66)', borderTopLeftRadius: 10, borderBottomLeftRadius: 10 }}/> */}
            {userSub === fire.cognito_user_id &&
            <img 
                src={optionsIcon}
                className='fire-card-options'
                onClick={() => {
                    setModal({
                        type: 'edit-and-delete',
                        content: fire
                    })
                }}
            />}
            <div className='fire-card-header'>
                <div className='fire-card-header-title'><img src={fireIcon} style={{ width: '1.5rem' }}/>{fire.name ? fire.name : 'Not Available'}</div>
                {/* <div className='fire-card-header-datasource'>
                    {getUploaderText} 
                    {fire.data_source !== 'Premium Users' && <img src={returnFlag((fire.data_source.includes('CWFIS') || fire.data_source.includes('CIFFC') ? 'Canada' : 'United States'))} style={{ width: 25, height: 25 }}/>}
                </div> */}
                <div className='fire-card-header-location-container'>
                    <div className='fire-card-header-location-container-inner'>
                        <div className='fire-card-header-location-container-inner-text'>
                            {!isVisible ? 'Loading...' : (location ? location : <Spinner />)}
                        </div>
                        {country && flagIcon && (
                         <img 
                             src={flagIcon} 
                             alt={`${country.toUpperCase()} flag`}
                             className='fire-card-header-location-container-flag'
                         />
                     )}
                    </div>
                </div>
            </div>
            <div className="fire-card-main">
                <div className="fire-card-main-left">
                    <div className="fire-card-main-left-subheader">
                        <div className="fire-card-main-left-subheader-status">
                            <div className="fire-card-main-left-subheader-status-title">Status</div>
                            <div
                                className="fire-card-main-left-subheader-status-value"
                                style={{
                                    color: fire.active ? '#f44336' : '#37c13e',
                                }}
                            >
                                {fire.active ? 'Active' : 'Contained'}
                            </div>
                        </div>
                        {fire.updatedTimestamp && (
                            <div className="fire-card-main-left-subheader-lastupdated">
                                <div className="fire-card-main-left-subheader-lastupdated-title">Last Updated</div>
                                <LiveTime value={fire.updatedTimestamp}/>
                            </div>
                        )}
                    </div>
                    <div className="fire-card-main-left-date">
                        <img src={calendar} className="fire-card-main-left-date-icon" alt="Calendar" />
                        <div className="fire-card-main-left-date-value">
                            {fire.startTimestamp
                                ? formatDate(fire.startTimestamp)
                                : fire.starttimestamp
                                ? formatDate(fire.starttimestamp)
                                : 'Not Available'
                            }
                        </div>
                    </div>
                    <div className="fire-card-main-left-location">
                        <img src={locationIcon} className="fire-card-main-left-location-icon" alt="Location" />
                        <div className="fire-card-main-left-location-value">
                            {`${fire.latitude.toFixed(5)}, ${fire.longitude.toFixed(5)}`}
                        </div>
                    </div>
                </div>
                <div className="fire-card-main-right">
                    <PieChart 
                        percentage={fire.perContained}
                        size={80}
                        type='containment'
                    />
                    <div className="fire-card-main-right-caption">Containment</div>
                </div>
            </div>
            {((fire.perContained || fire.perContained === 0) && fire.last_containment && (fire.last_containment.percontained || fire.last_containment.percontained === 0) && fire.last_containment.timestamp) && (fire.perContained - fire.last_containment.percontained !== 0) &&
                        <div className='fire-card-main-left-containment-update'>
                            <WarningCircle size={24} />
                            <div className='fire-card-main-left-containment-update-text'>
                                {`Fire containment has `}
                                <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'black' }}>{fireContainmentChange(fire.last_containment.percontained, fire.perContained)}</span>
                                <span 
                                    style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'black' }}
                                >
                                    {` in the span of ${timeDifference(fire.last_containment.timestamp, fire.updatedTimestamp ? fire.updatedTimestamp : fire.startTimestamp)}.`}
                                </span>
                            </div>
                        </div>}
            <div className="fire-card-btns">
                <div 
                    className="fire-card-btns-btn-view"
                    onClick={goToFire}    
                    >
                    View
                </div>
                {hasAlerts ?
                <div 
                    className='fire-card-btns-btn-alerts-on'
                    onClick={() => {
                        setModal({
                        type: 'alerts',
                        content: {
                            setModal: setModal,
                            fireId: fire.fire_id
                        }
                        })
                    }}
                    >
                    <BellSimpleRingingIcon size={16} weight='fill'/>
                    Alerts On
                    </div>
                    :
                    <div 
                    className='fire-card-btns-btn-alerts-off'
                    onClick={() => {
                        setModal({
                            type: 'alerts',
                            content: {
                                setModal: setModal,
                                fireId: fire.fire_id
                            }
                        })
                    }}
                >
                    <BellSimpleRingingIcon size={16} weight='fill'/>
                    Turn On Alerts
                </div>}
            </div>
        </div>
    );
}